import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { FormField, FormTemplate, UserType } from '../interfaces/forms';
import { createFormTemplate, updateFormTemplate } from '../../../api/formsApi';
import { useEsteiras } from '../../../hooks/useEsteiras';
import {
  fieldNeedsOptions,
  fieldNeedsScale,
  formFieldTypeLabel,
  formFieldTypeList,
  papeisComAcessoAFormularios,
  userTypeLabel,
} from '../../../modules/formularios/types/formFields';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import { notify } from '../../../shared/feedback/notify';
import '../formularios.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface FormTemplateModalProps {
  isOpen: boolean;
  template: FormTemplate | 'new' | null;
  onClose: () => void;
  onSaved: () => void;
}

const emptyField = (order: number): FormField => ({
  label: '',
  type: 'TEXT_SHORT',
  options: [],
  required: false,
  order,
});

const FormTemplateModal: React.FC<FormTemplateModalProps> = ({ isOpen, template, onClose, onSaved }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [allowedRoles, setAllowedRoles] = useState<UserType[]>([]);
  const [requiresCar, setRequiresCar] = useState(false);
  const [triggerEsteiraId, setTriggerEsteiraId] = useState<string>('');
  const [respondentePublico, setRespondentePublico] = useState(false);
  const { esteirasAtivas } = useEsteiras();

  const [fields, setFields] = useState<FormField[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // at-67 (BUG-V30): papéis já autorizados que não alcançam a tela (dados legados).
  const papeisSemAcessoMarcados = allowedRoles.filter(
    (role) => role !== 'ADMIN' && !papeisComAcessoAFormularios.includes(role),
  );

  const isEditing = template !== 'new' && template !== null;
  const hasResponses = isEditing && (template as FormTemplate)._count?.responses ? (template as FormTemplate)._count!.responses > 0 : false;

  useEffect(() => {
    if (!isOpen) return;

    if (template === 'new' || template === null) {
      setTitle('');
      setDescription('');
      setAllowedRoles([]);
      setRequiresCar(false);
      setTriggerEsteiraId('');
      setRespondentePublico(false);
      setFields([emptyField(0)]);
    } else {
      setTitle(template.title);
      setDescription(template.description || '');
      setAllowedRoles(template.allowedRoles);
      setRequiresCar(template.requiresCar);
      // Sem isto a caixa abriria desmarcada num formulário que É a pesquisa, e o
      // primeiro salvamento a desligaria em silêncio, derrubando o link da recepção.
      setRespondentePublico(Boolean(template.respondentePublico));
      // Registro anterior à regra pode ter as duas coisas. Como o campo de gatilho
      // fica escondido na pesquisa, manter o valor no estado criaria um beco sem
      // saída: o backend recusaria o salvamento e não haveria campo para corrigir.
      setTriggerEsteiraId(template.respondentePublico ? '' : template.triggerEsteiraId || '');
      setFields(template.fields.length > 0 ? template.fields : [emptyField(0)]);
    }
  }, [isOpen, template]);

  if (!isOpen) return null;

  const toggleRole = (role: UserType) => {
    setAllowedRoles((prev) => (prev.includes(role) ? prev.filter((item) => item !== role) : [...prev, role]));
  };

  const updateField = (index: number, changes: Partial<FormField>) => {
    setFields((prev) => prev.map((field, i) => (i === index ? { ...field, ...changes } : field)));
  };

  const addField = () => {
    setFields((prev) => [...prev, emptyField(prev.length)]);
  };

  const removeField = (index: number) => {
    setFields((prev) => prev.filter((_, i) => i !== index).map((field, i) => ({ ...field, order: i })));
  };

  const moveField = (index: number, direction: -1 | 1) => {
    setFields((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((field, i) => ({ ...field, order: i }));
    });
  };

  // Mantém as linhas cruas durante a digitação (inclusive vazias) — sanear aqui
  // "engoliria" o Enter, porque o textarea é controlado por options.join('\n').
  // A limpeza (trim + remover vazias) acontece só no salvar.
  const handleOptionsChange = (index: number, value: string) => {
    updateField(index, { options: value.split('\n') });
  };

  const sanitizeOptions = (options?: string[]) =>
    (options || []).map((option) => option.trim()).filter((option) => option.length > 0);

  /**
   * at-67 (BUG-V62): rótulos das pontas da escala, gravados em `options[0]` e `options[1]`.
   *
   * O preenchimento já exibia esses dois valores como legendas do mínimo e do máximo
   * (`FillFormModal`), mas **não havia nenhuma UI para cadastrá-los** — funcionalidade
   * inalcançável pela tela. O armazenamento reaproveita `options`, que na escala não tinha
   * uso.
   *
   * ⚠️ A sanitização é POSICIONAL, ao contrário da das opções de escolha: `sanitizeOptions`
   * remove as vazias, então preencher só o rótulo do máximo faria ele **deslizar para o
   * índice 0** e virar o rótulo do mínimo. Aqui a posição É o significado.
   */
  const sanitizeScaleLabels = (options?: string[]) => {
    const minimo = (options?.[0] ?? '').trim();
    const maximo = (options?.[1] ?? '').trim();
    if (!minimo && !maximo) return [];
    return [minimo, maximo];
  };

  const updateScaleLabel = (index: number, posicao: 0 | 1, value: string) => {
    const atuais = fields[index].options || [];
    const proximas = [atuais[0] ?? '', atuais[1] ?? ''];
    proximas[posicao] = value;
    updateField(index, { options: proximas });
  };

  /**
   * Trocar o tipo do campo limpa as opções. Sem isso, um campo que era "Seleção única"
   * com 5 opções e vira "Escala numérica" levaria as duas primeiras opções antigas como
   * rótulos das pontas — texto aleatório aparecendo na tela de quem responde.
   */
  const changeFieldType = (index: number, type: FormField['type']) => {
    updateField(index, { type, options: [] });
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.warning('Informe o título do formulário.');
      return;
    }

    // Na pesquisa do associado quem responde não tem login: exigir um perfil
    // obrigaria a autorizar alguém que não vai responder. A lista segue opcional
    // ali, governando apenas quem, além do ADMIN, vê as respostas.
    if (allowedRoles.length === 0 && !respondentePublico) {
      toast.warning('Selecione ao menos um perfil autorizado.');
      return;
    }

    // Payload só com as propriedades do FormFieldDto do backend — campos
    // carregados na edição trazem id/templateId, que o whitelist rejeita (400).
    const sanitizedFields = fields.map((field) => ({
      label: field.label,
      type: field.type,
      // Escala usa `options` para os rótulos das pontas, e ali a POSIÇÃO é o significado.
      options: fieldNeedsScale(field.type) ? sanitizeScaleLabels(field.options) : sanitizeOptions(field.options),
      scaleMin: field.scaleMin,
      scaleMax: field.scaleMax,
      required: field.required || false,
      order: field.order,
    }));

    if (!hasResponses) {
      if (sanitizedFields.length === 0) {
        toast.warning('Adicione ao menos um campo ao formulário.');
        return;
      }

      for (const field of sanitizedFields) {
        if (!field.label.trim()) {
          toast.warning('Todos os campos precisam de um rótulo.');
          return;
        }
        if (fieldNeedsOptions(field.type) && (!field.options || field.options.length === 0)) {
          toast.warning(`O campo "${field.label}" precisa de opções.`);
          return;
        }
        if (fieldNeedsScale(field.type) && (field.scaleMin === undefined || field.scaleMax === undefined)) {
          toast.warning(`O campo "${field.label}" precisa de valores mínimo e máximo da escala.`);
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      if (template === 'new' || template === null) {
        await createFormTemplate({
          title: title.trim(),
          description: description.trim() || undefined,
          allowedRoles,
          requiresCar,
          triggerEsteiraId: triggerEsteiraId || undefined,
          respondentePublico,
          fields: sanitizedFields,
        });
        toast.success('Formulário criado com sucesso.');
      } else {
        await updateFormTemplate(template.id, {
          title: title.trim(),
          description: description.trim() || undefined,
          allowedRoles,
          requiresCar,
          triggerEsteiraId: triggerEsteiraId || null,
          respondentePublico,
          ...(hasResponses ? {} : { fields: sanitizedFields }),
        });
        toast.success('Formulário atualizado com sucesso.');
      }
      onSaved();
    } catch (err) {
      // at-64 (BUG-V25): idem FillFormModal — `err.message` do axios é o status cru
      // ("Request failed with status code 400"); a razão real vem no corpo da resposta.
      const criando = template === 'new' || template === null;
      notify.actionError(err, { action: criando ? 'create' : 'update', subject: 'o formulário' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {submitting && <FullScreenLoader />}
      <div className="formularios-modal-overlay" onClick={onClose}>
        <div className="formularios-modal formularios-modal-large" onClick={(e) => e.stopPropagation()}>
          <div className="formularios-modal-header">
            <h2 className="formularios-modal-title">
              {template === 'new' || template === null ? 'Novo Formulário' : 'Editar Formulário'}
            </h2>
            <button className="close-button" type="button" onClick={onClose}>&times;</button>
          </div>

          <div className="formularios-modal-body">
            <div className="formularios-form-group">
              <label className="formularios-label">Título</label>
              <input
                type="text"
                className="formularios-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="formularios-form-group">
              <label className="formularios-label">Descrição</label>
              <textarea
                className="formularios-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="formularios-form-group">
              <label className="formularios-label">
                {respondentePublico ? 'Perfis autorizados a responder (opcional)' : 'Perfis autorizados a responder'}
              </label>
              {respondentePublico && (
                <div className="formularios-hint" style={{ marginBottom: 10 }}>
                  Quem responde esta pesquisa é o associado, pelo link — ele não tem login e não depende
                  desta lista. Marque um perfil aqui só para que ele também possa responder pela tela e{' '}
                  <strong>ver as respostas recebidas</strong>. Deixando em branco, apenas administradores
                  acompanham os resultados.
                </div>
              )}
              <div className="formularios-checkbox-group">
                {papeisComAcessoAFormularios.map((role) => (
                  <label key={role} className="formularios-checkbox-item">
                    <input
                      type="checkbox"
                      checked={allowedRoles.includes(role)}
                      onChange={() => toggleRole(role)}
                    />
                    {userTypeLabel[role]}
                  </label>
                ))}
              </div>

              {/*
                at-67 (BUG-V30): papel marcado que NÃO alcança a tela de Formulários.
                Os três (Relacionamento, Contac, Administração) eram oferecidos no
                construtor e nunca conseguiriam responder. Eles saíram da lista acima, mas
                os que já ficaram marcados precisam continuar VISÍVEIS — escondê-los
                deixaria a autorização inútil gravada em silêncio, sem caminho para tirar.
              */}
              {papeisSemAcessoMarcados.length > 0 && (
                <div className="formularios-warning" style={{ marginTop: 10 }}>
                  <strong>Estes perfis não têm acesso à tela de Formulários</strong> e não conseguem responder,
                  mesmo autorizados aqui. Desmarque-os para deixar a autorização coerente:
                  <div className="formularios-checkbox-group" style={{ marginTop: 8 }}>
                    {papeisSemAcessoMarcados.map((role) => (
                      <label key={role} className="formularios-checkbox-item">
                        <input type="checkbox" checked onChange={() => toggleRole(role)} />
                        {userTypeLabel[role]}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="formularios-form-group">
              <label className="formularios-checkbox-item">
                <input
                  type="checkbox"
                  checked={requiresCar}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setRequiresCar(checked);
                    if (!checked) {
                      setTriggerEsteiraId('');
                      // A pesquisa do associado é sempre sobre um atendimento: sem
                      // veículo o backend recusa, então a caixa acompanha.
                      setRespondentePublico(false);
                    }
                  }}
                />
                Vincular resposta a um veículo
              </label>
            </div>

            {requiresCar && (
              <div className="formularios-form-group">
                <label className="formularios-checkbox-item">
                  <input
                    type="checkbox"
                    checked={respondentePublico}
                    onChange={(e) => {
                      const marcado = e.target.checked;
                      setRespondentePublico(marcado);
                      // O gatilho não vale para a pesquisa (o backend recusa): limpar
                      // aqui evita enviar um valor que ficou escondido na tela.
                      if (marcado) setTriggerEsteiraId('');
                    }}
                  />
                  Pesquisa respondida pelo associado (link enviado por WhatsApp)
                </label>

                <div className="formularios-hint">
                  Marcando esta opção, a Recepção e o Chefe de Oficina passam a ver a ação
                  <strong> Enviar Pesquisa de Satisfação</strong> na aba Entregues: o associado recebe um
                  link, confirma o celular cadastrado e responde sem precisar de login. Ele também pode
                  responder no <strong>totem da oficina</strong>, digitando placa e celular. Cada atendimento
                  aceita uma resposta (somando os dois canais), e o veículo precisa ter telefone do
                  proprietário cadastrado. Apenas um formulário ativo por vez pode ser a pesquisa do associado.
                  <br />
                  <br />
                  Ela <strong>não usa gatilho de esteira</strong> e <strong>não pode ser preenchida por
                  dentro</strong> — nem por administradores. Só o associado responde, pelo link, para que
                  nenhuma resposta seja registrada no lugar dele.
                </div>
              </div>
            )}

            {/*
              O gatilho some quando é a pesquisa do associado: ele não envia nada a
              ele (o link é manual) e só criaria pendência interna — que, na esteira
              natural (Entrega, terminal), nunca expiraria e convidaria alguém a
              responder no lugar do cliente. O backend recusa a combinação.
            */}
            {requiresCar && !respondentePublico && (
              <div className="formularios-form-group">
                <label className="formularios-label">Gatilho automático (opcional)</label>
                <select
                  className="formularios-input"
                  value={triggerEsteiraId}
                  onChange={(e) => setTriggerEsteiraId(e.target.value)}
                >
                  <option value="">Nenhum</option>
                  {esteirasAtivas.map((est) => (
                    <option key={est.id} value={est.id}>{est.nome}</option>
                  ))}
                </select>

                <div className="formularios-hint">
                  Ao entrar nesta esteira, carros sem resposta para este formulário aparecerão como pendentes para os perfis autorizados.
                </div>
              </div>
            )}

            <div className="formularios-form-group">
              <label className="formularios-label">Campos do formulário</label>

              {hasResponses && (
                <div className="formularios-warning">
                  Este formulário já possui respostas. A estrutura de campos não pode mais ser alterada — duplique o
                  formulário para criar uma nova versão.
                </div>
              )}

              {fields.map((field, index) => (
                <div className="formularios-field-card" key={field.id || `new-${index}`}>
                  <div className="formularios-field-card-header">
                    <span className="formularios-field-card-title">Campo {index + 1}</span>
                    {!hasResponses && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          className="formularios-icon-btn"
                          onClick={() => moveField(index, -1)}
                          disabled={index === 0}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="formularios-icon-btn"
                          onClick={() => moveField(index, 1)}
                          disabled={index === fields.length - 1}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="formularios-icon-btn"
                          onClick={() => removeField(index)}
                          disabled={fields.length === 1}
                        >
                          Remover
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="formularios-field-row">
                    <div>
                      <label className="formularios-label">Rótulo</label>
                      <input
                        type="text"
                        className="formularios-input"
                        value={field.label}
                        disabled={hasResponses}
                        onChange={(e) => updateField(index, { label: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="formularios-label">Tipo</label>
                      <select
                        className="formularios-select"
                        value={field.type}
                        disabled={hasResponses}
                        onChange={(e) => changeFieldType(index, e.target.value as FormField['type'])}
                      >
                        {formFieldTypeList.map((type) => (
                          <option key={type} value={type}>{formFieldTypeLabel[type]}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {fieldNeedsOptions(field.type) && (
                    <div className="formularios-form-group">
                      <label className="formularios-label">Opções (uma por linha)</label>
                      <textarea
                        className="formularios-textarea"
                        value={(field.options || []).join('\n')}
                        disabled={hasResponses}
                        onChange={(e) => handleOptionsChange(index, e.target.value)}
                      />
                    </div>
                  )}

                  {fieldNeedsScale(field.type) && (
                    <>
                      <div className="formularios-field-row">
                        <div>
                          <label className="formularios-label">Valor mínimo</label>
                          <input
                            type="number"
                            className="formularios-input"
                            value={field.scaleMin ?? ''}
                            disabled={hasResponses}
                            onChange={(e) => updateField(index, { scaleMin: e.target.value === '' ? undefined : Number(e.target.value) })}
                          />
                        </div>
                        <div>
                          <label className="formularios-label">Valor máximo</label>
                          <input
                            type="number"
                            className="formularios-input"
                            value={field.scaleMax ?? ''}
                            disabled={hasResponses}
                            onChange={(e) => updateField(index, { scaleMax: e.target.value === '' ? undefined : Number(e.target.value) })}
                          />
                        </div>
                      </div>

                      {/* at-67 (BUG-V62): quem responde já via estes rótulos; não havia
                          onde cadastrá-los. */}
                      <div className="formularios-field-row">
                        <div>
                          <label className="formularios-label">Rótulo do valor mínimo (opcional)</label>
                          <input
                            type="text"
                            className="formularios-input"
                            placeholder="Ex: Muito insatisfeito"
                            value={field.options?.[0] ?? ''}
                            disabled={hasResponses}
                            onChange={(e) => updateScaleLabel(index, 0, e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="formularios-label">Rótulo do valor máximo (opcional)</label>
                          <input
                            type="text"
                            className="formularios-input"
                            placeholder="Ex: Muito satisfeito"
                            value={field.options?.[1] ?? ''}
                            disabled={hasResponses}
                            onChange={(e) => updateScaleLabel(index, 1, e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="formularios-hint">
                        Os rótulos aparecem sob as pontas da escala para quem responde, explicando o que o menor e
                        o maior valor significam.
                      </div>
                    </>
                  )}

                  <label className="formularios-checkbox-item">
                    <input
                      type="checkbox"
                      checked={field.required || false}
                      disabled={hasResponses}
                      onChange={(e) => updateField(index, { required: e.target.checked })}
                    />
                    Campo obrigatório
                  </label>
                </div>
              ))}

              {!hasResponses && (
                <button type="button" className="formularios-btn" onClick={addField}>
                  + Adicionar campo
                </button>
              )}
            </div>
          </div>

          <div className="formularios-modal-footer">
            <button className="formularios-btn" type="button" onClick={onClose}>Cancelar</button>
            <button className="formularios-btn primary" type="button" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormTemplateModal;
